import {deleteCookie, getCookie, setCookie} from '@github-ui/cookies'
import {useState} from 'react'

import {persistPullsPreferences} from '../helpers/pulls-preferences-api'

const COOKIE_NAME = 'dashboard_surface_pulls_default_view'
const COOKIE_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000 // 1 year

/**
 * Hook to manage and persist the default pulls view setting via a browser cookie.
 *
 * The cookie value is either a known view slug (e.g. "inbox", "authored",
 * "assigned", "involves", "reviews", "on-my-behalf") or the stringified
 * numeric id of one of the user's pull-request saved views (e.g. "4217").
 * The server silently falls back to the inbox when the cookie value is unknown
 * or refers to a saved view the user no longer owns.
 *
 * @param currentViewId The id (or slug) of the currently displayed view, used
 *   when the user opts in to "Set as default view".
 * @param defaultViewId The server-provided default view id used as a fallback
 *   when no cookie has been written by this client yet.
 */
export function useSaveDefaultPullsView(currentViewId?: string, defaultViewId?: string) {
  const [isSaving, setIsSaving] = useState(false)
  const [savedDefaultViewId, setSavedDefaultViewId] = useState<string | undefined>(() => {
    return getDefaultPullsView() ?? (defaultViewId != null ? String(defaultViewId) : undefined)
  })

  const defaultViewValue = currentViewId != null && currentViewId !== '' ? String(currentViewId) : 'inbox'
  const isDefault = defaultViewValue === (savedDefaultViewId || 'inbox')

  async function saveDefaultPullsView() {
    if (isDefault) return

    setIsSaving(true)
    try {
      const expireTime = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString()
      setCookie(COOKIE_NAME, defaultViewValue, expireTime)
      setSavedDefaultViewId(defaultViewValue)
      persistPullsPreferences({default_view: defaultViewValue})
    } finally {
      setIsSaving(false)
    }
  }

  async function clearDefaultPullsView() {
    setIsSaving(true)
    try {
      deleteCookie(COOKIE_NAME)
      setSavedDefaultViewId(undefined)
      persistPullsPreferences({default_view: ''})
    } finally {
      setIsSaving(false)
    }
  }

  return {isSaving, isDefault, saveDefaultPullsView, clearDefaultPullsView}
}

/**
 * Get the saved default pulls view from the cookie.
 * @returns The saved view value, or undefined if not set
 */
export function getDefaultPullsView(): string | undefined {
  return getCookie(COOKIE_NAME)?.value
}
